import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Loader2, Upload, X, Check, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { toast } from "sonner";

const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED = "image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf";

/**
 * OCR upload dialog — reads a photo of an invoice/ticket using GPT-4o Vision
 * and prefills a new invoice draft via sessionStorage.
 *
 * props:
 *   kind: "invoice" | "quote"
 */
export default function OCRUploadDialog({ kind = "invoice" }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setPreviewUrl((u) => {
      if (u) URL.revokeObjectURL(u);
      return null;
    });
    setResult(null);
    setBusy(false);
  };

  const onPick = (f) => {
    if (!f) return;
    if (f.size > MAX_SIZE) {
      toast.error(lang === "es" ? "Imagen demasiado grande (máx. 10 MB)" : "Imatge massa gran (màx. 10 MB)");
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
  };

  const onUpload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/ai/ocr-invoice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setResult(r.data.structured);
      toast.success(lang === "es" ? "Datos extraídos" : "Dades extretes");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const onUseData = () => {
    if (!result) return;
    // Save to sessionStorage so the editor can prefill
    sessionStorage.setItem("hosys_ocr_prefill", JSON.stringify({
      kind,
      data: result,
      timestamp: Date.now(),
    }));
    setOpen(false);
    reset();
    navigate(`/app/${kind === "invoice" ? "invoices" : "quotes"}/new`);
  };

  const closeAll = () => {
    if (busy) return;
    setOpen(false);
    reset();
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-blue-400/30 text-blue-300 hover:bg-blue-500/10"
        data-testid={`${kind}-ocr-upload-btn`}
      >
        <Camera size={16} className="mr-2" />
        {lang === "es" ? "Escanear factura" : "Escanejar factura"}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150"
          onClick={closeAll}
        >
          <div
            className="w-full max-w-2xl bg-[#0B1220] border border-white/10 rounded-2xl shadow-2xl p-6 animate-in zoom-in-95 duration-150 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="ocr-dialog"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-white">
                    {lang === "es" ? "Escanear factura con IA" : "Escanejar factura amb IA"}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {lang === "es"
                      ? "Sube una foto y la IA extraerá los datos"
                      : "Puja una foto i la IA n'extraurà les dades"}
                  </p>
                </div>
              </div>
              <button
                onClick={closeAll}
                disabled={busy}
                className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400 disabled:opacity-50"
              >
                <X size={16} />
              </button>
            </div>

            {/* Step 1: Pick file */}
            {!file && (
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  onPick(e.dataTransfer.files?.[0]);
                }}
                className="border-2 border-dashed border-white/15 rounded-xl p-10 text-center cursor-pointer hover:border-blue-400/50 hover:bg-blue-500/5 transition-colors"
                data-testid="ocr-dropzone"
              >
                <Upload className="w-10 h-10 mx-auto text-slate-500 mb-3" />
                <p className="text-sm text-slate-300 font-medium mb-1">
                  {lang === "es"
                    ? "Pulsa o arrastra una imagen o PDF aquí"
                    : "Clica o arrossega una imatge o PDF aquí"}
                </p>
                <p className="text-xs text-slate-500">
                  JPG, PNG, WEBP, HEIC, PDF · {lang === "es" ? "máx." : "màx."} 10 MB
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPTED}
                  capture="environment"
                  className="hidden"
                  onChange={(e) => onPick(e.target.files?.[0])}
                  data-testid="ocr-file-input"
                />
              </div>
            )}

            {/* Step 2: Preview + analyze */}
            {file && !result && (
              <div className="space-y-4">
                <div className="relative rounded-xl overflow-hidden bg-black border border-white/10 min-h-[200px] flex items-center justify-center">
                  {file.type === "application/pdf" ? (
                    <div className="text-center p-8">
                      <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-rose-500/20 to-orange-500/20 border border-rose-500/30 flex items-center justify-center">
                        <span className="font-bold text-rose-300 text-xs tracking-wider">PDF</span>
                      </div>
                      <p className="text-sm text-slate-300 font-medium">{file.name}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {lang === "es"
                          ? "Se analizará la primera página"
                          : "S'analitzarà la primera pàgina"}
                      </p>
                    </div>
                  ) : (
                    <img
                      src={previewUrl}
                      alt="preview"
                      className="w-full max-h-[400px] object-contain"
                    />
                  )}
                  <button
                    onClick={() => reset()}
                    disabled={busy}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-slate-400 truncate">
                    {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
                  </span>
                  <Button
                    onClick={onUpload}
                    disabled={busy}
                    data-testid="ocr-analyze-btn"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {busy ? (
                      <>
                        <Loader2 size={14} className="mr-2 animate-spin" />
                        {lang === "es" ? "Analizando..." : "Analitzant..."}
                      </>
                    ) : (
                      <>
                        <Camera size={14} className="mr-2" />
                        {lang === "es" ? "Analizar con IA" : "Analitzar amb IA"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Show extracted data */}
            {result && (
              <div className="space-y-4" data-testid="ocr-result">
                <ConfidenceBanner confidence={result.confidence} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <Field
                    label={lang === "es" ? "Cliente / Receptor" : "Client / Receptor"}
                    value={result.client_name}
                  />
                  <Field
                    label={lang === "es" ? "NIF/CIF" : "NIF/CIF"}
                    value={result.client_nif}
                  />
                  <Field
                    label={lang === "es" ? "Nº documento" : "Nº document"}
                    value={result.document_number}
                  />
                  <Field
                    label={lang === "es" ? "Fecha" : "Data"}
                    value={result.date}
                  />
                  <Field
                    label={lang === "es" ? "Subtotal" : "Subtotal"}
                    value={formatEUR(result.subtotal)}
                  />
                  <Field
                    label="IVA"
                    value={formatEUR(result.vat_amount)}
                  />
                  <Field
                    label="Total"
                    value={formatEUR(result.total)}
                    bold
                  />
                </div>

                {result.items?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">
                      {lang === "es" ? "Conceptos extraídos" : "Conceptes extrets"}
                    </div>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {result.items.map((it, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-3 text-sm p-2.5 bg-white/[0.03] rounded-lg border border-white/5"
                        >
                          <span className="text-slate-500 shrink-0">{i + 1}.</span>
                          <span className="flex-1 text-slate-200 leading-snug">
                            {it.description || "—"}
                          </span>
                          <span className="text-slate-400 font-mono shrink-0">
                            {formatEUR(it.unit_price * (it.quantity || 1))}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
                  <Button
                    variant="ghost"
                    onClick={() => reset()}
                    className="text-slate-400 hover:text-white"
                  >
                    {lang === "es" ? "Otra imagen" : "Una altra imatge"}
                  </Button>
                  <Button
                    onClick={onUseData}
                    data-testid="ocr-use-data-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Check size={14} className="mr-2" />
                    {lang === "es" ? "Usar estos datos" : "Usar aquestes dades"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, value, bold = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className={`text-slate-200 ${
          bold ? "font-bold text-base" : "text-sm"
        } truncate`}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function ConfidenceBanner({ confidence }) {
  const c = Number(confidence || 0);
  if (c >= 0.7) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs">
        <Check size={14} />
        Confiança alta ({Math.round(c * 100)}%) — Revisa-ho ràpidament i continua
      </div>
    );
  }
  if (c >= 0.4) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs">
        <AlertCircle size={14} />
        Confiança mitjana ({Math.round(c * 100)}%) — Revisa amb cura les dades
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs">
      <AlertCircle size={14} />
      Confiança baixa ({Math.round(c * 100)}%) — La imatge potser no era clara
    </div>
  );
}

function formatEUR(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("ca-ES", {
    style: "currency",
    currency: "EUR",
  }).format(Number(n) || 0);
}
